import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatVietnamCurrency } from '../../utils/numberFormatter';
import { FieldRow } from './FieldRow';

interface LinkedRecord {
    id: number;
    [key: string]: any;
}

interface LinkedRecordsSectionProps {
    repairRecords: LinkedRecord[];
    fuelReceipts: LinkedRecord[];
    prepaidRecords: LinkedRecord[];
    pendingRecordId: number | null;
    isEditMode: boolean;
    numericId?: number;
    onUnlinkRepair: (id: number) => void;
    onUnlinkFuel: (id: number) => void;
    onUnlinkPrepaid: (id: number) => void;
    onShowRepairModal: () => void;
    onShowFuelModal: () => void;
    onShowPrepaidModal: () => void;
    onSetPendingRecordId: (id: number) => void;
}

const LinkedRecordsSection: React.FC<LinkedRecordsSectionProps> = ({
    repairRecords,
    fuelReceipts,
    prepaidRecords,
    pendingRecordId,
    isEditMode,
    numericId,
    onUnlinkRepair,
    onUnlinkFuel,
    onUnlinkPrepaid,
    onShowRepairModal,
    onShowFuelModal,
    onShowPrepaidModal,
    onSetPendingRecordId
}) => {
    const { t } = useLanguage();

    return (
        <>
            <FieldRow label={t('linked.repair.records')}>
                <div>
                    {repairRecords.length === 0 ? (
                        <p className="text-muted">{t('no.repair.records.linked')}</p>
                    ) : (
                        <div className="mb-2">
                            <table className="table table-bordered table-sm align-middle" style={{ width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th>{t('description')}</th>
                                        <th>{t('cost')}</th>
                                        <th>{t('repair.date')}</th>
                                        <th>{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {repairRecords.map(repair => (
                                        <tr key={repair.id}>
                                            <td>{repair.description || t('no.description')}</td>
                                            <td>{formatVietnamCurrency(repair.cost)}</td>
                                            <td>{new Date(repair.repairDate).toLocaleDateString()}</td>
                                            <td>
                                                <button 
                                                    type="button" 
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => onUnlinkRepair(repair.id)}
                                                >
                                                    {t('unlink')}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {repairRecords.length > 0 && (
                                        <tr>
                                            <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                                {t('total.repair.cost')}: {formatVietnamCurrency(repairRecords.reduce((sum, r) => sum + r.cost, 0))}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div style={{ textAlign: 'right' }}>
                        <button 
                            type="button" 
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                                if (isEditMode && numericId && !pendingRecordId) {
                                    onSetPendingRecordId(numericId);
                                }
                                onShowRepairModal();
                            }}
                            disabled={!pendingRecordId}
                            title={!pendingRecordId ? t('please.save.record.first') : ''}
                        >
                            {t('link.repair.receipt')}
                        </button>
                        {!pendingRecordId && (
                            <div className="text-danger mt-1" style={{ fontSize: '0.9em' }}>
                                {t('please.save.record.first')}
                            </div>
                        )}
                    </div>
                </div>
            </FieldRow>

            <FieldRow label={t('linked.fuel.receipts')}>
                <div>
                    {fuelReceipts.length === 0 ? (
                        <p className="text-muted">{t('no.fuel.receipts.linked')}</p>
                    ) : (
                        <div className="mb-2">
                            <table className="table table-bordered table-sm align-middle" style={{ width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th>{t('gas.station')}</th>
                                        <th>{t('liters')}</th>
                                        <th>{t('total.amount')}</th>
                                        <th>{t('date')}</th>
                                        <th>{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fuelReceipts.map(fuel => (
                                        <tr key={fuel.id}>
                                            <td>{fuel.gasStation || 'Unknown station'}</td>
                                            <td>{fuel.liters}</td>
                                            <td>{formatVietnamCurrency(fuel.totalAmount)}</td>
                                            <td>{new Date(fuel.refuelDate).toLocaleDateString()}</td>
                                            <td>
                                                <button 
                                                    type="button" 
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => onUnlinkFuel(fuel.id)}
                                                >
                                                    {t('unlink')}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {fuelReceipts.length > 0 && (
                                        <tr>
                                            <td colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                                {t('total.fuel.cost')}: {formatVietnamCurrency(fuelReceipts.reduce((sum, f) => sum + f.totalAmount, 0))}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div style={{ textAlign: 'right' }}>
                        <button 
                            type="button" 
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                                if (!pendingRecordId && numericId) {
                                    onSetPendingRecordId(numericId);
                                }
                                onShowFuelModal();
                            }}
                            disabled={!pendingRecordId}
                            title={!pendingRecordId ? t('please.save.record.first') : ''}
                        >
                            {t('link.fuel.receipt')}
                        </button>
                        {!pendingRecordId && (
                            <div className="text-danger mt-1" style={{ fontSize: '0.9em' }}>
                                {t('please.save.record.first')}
                            </div>
                        )}
                    </div>
                </div>
            </FieldRow>

            <FieldRow label={t('linked.prepaid.records')}>
                <div>
                    {prepaidRecords.length === 0 ? (
                        <p className="text-muted">{t('no.prepaid.records.linked')}</p>
                    ) : (
                        <div className="mb-2">
                            <table className="table table-bordered table-sm align-middle" style={{ width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th>{t('driver')}</th>
                                        <th>{t('prepaid.date')}</th>
                                        <th>{t('amount')}</th>
                                        <th>{t('description')}</th>
                                        <th>{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prepaidRecords.map(prepaid => (
                                        <tr key={prepaid.id}>
                                            <td>{prepaid.driverName}</td>
                                            <td>{new Date(prepaid.prepaidDate).toLocaleDateString()}</td>
                                            <td>{formatVietnamCurrency(prepaid.amount)}</td>
                                            <td>{prepaid.description || '-'}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => onUnlinkPrepaid(prepaid.id)}
                                                >
                                                    {t('unlink')}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {prepaidRecords.length > 0 && (
                                        <tr>
                                            <td colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                                {t('total.prepaid.amount')}: {formatVietnamCurrency(prepaidRecords.reduce((sum, p) => sum + p.amount, 0))}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div style={{ textAlign: 'right' }}>
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                                if (isEditMode && numericId && !pendingRecordId) {
                                    onSetPendingRecordId(numericId);
                                }
                                onShowPrepaidModal();
                            }}
                            disabled={!pendingRecordId}
                            title={!pendingRecordId ? t('please.save.record.first') : ''}
                        >
                            {t('link.prepaid.record')}
                        </button>
                        {!pendingRecordId && (
                            <div className="text-danger mt-1" style={{ fontSize: '0.9em' }}>
                                {t('please.save.record.first')}
                            </div>
                        )}
                    </div>
                </div>
            </FieldRow>
        </>
    );
};

export default LinkedRecordsSection;
